import { useDialog } from '@/contexts/DialogContext';
import { useToast } from '@/contexts/ToastContext';
import { useTranslation } from '@client-app/i18n';
import { Tooltip } from '@common/index';
import {
    CaretDown,
    Check,
    CircleNotch,
    PencilSimple,
    Plus,
    Trash,
} from '@phosphor-icons/react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Modal from './Modal';
import {
    Role,
} from './rolesData';
import {
    getDeletionReplacementRoleOptions,
    isDeletionReplacementRoleOption,
} from './roleAccessGuards';
import { canEditRoleMetadata } from './roleMetadataGuards';
import { hasVisibleDuplicateRoleNameError } from './roleNameValidation';
import { formatRoleLabel } from './roleLabels';

const ReplacementRoleDropdown = ({
    value,
    onChange,
    options,
    disabled,
}: {
    value: string;
    onChange: (nextRoleId: string) => void;
    options: Role[];
    disabled?: boolean;
}) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const safeOptions = useMemo(
        () => options.filter(role => isDeletionReplacementRoleOption(role)),
        [options]
    );
    const selectedRole = safeOptions.find(role => role.id === value) || null;
    const hasOptions = safeOptions.length > 0;

    return (
        <DropdownMenu.Root
            open={open}
            onOpenChange={(nextOpen) => {
                if (disabled || !hasOptions) {
                    return;
                }
                setOpen(nextOpen);
            }}
        >
            <DropdownMenu.Trigger asChild>
                <button
                    type="button"
                    disabled={disabled || !hasOptions}
                    className={clsx(
                        "flex min-w-0 w-full items-center justify-between rounded-[10px] border bg-white px-3 py-2.5 text-left text-sm transition-all",
                        selectedRole ? "text-text-main" : "text-text-sub",
                        open
                            ? "border-[#1A162F] bg-[#F8F9FC] ring-2 ring-[#1A162F]/20"
                            : "border-[#E8EBF3] hover:border-[#C8D0E2]",
                        (disabled || !hasOptions) && "cursor-not-allowed opacity-60"
                    )}
                >
                    <span
                        className="truncate pr-2 font-normal"
                        title={selectedRole ? formatSystemRoleLabel(selectedRole.name, t) : t('website.membersSettings.selectRole')}
                    >
                        {selectedRole ? formatSystemRoleLabel(selectedRole.name, t) : t('website.membersSettings.selectRole')}
                    </span>
                    <CaretDown
                        size={14}
                        weight="regular"
                        className={clsx("shrink-0 text-[#8A93B2] transition-transform duration-200", open && "rotate-180")}
                    />
                </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
                <DropdownMenu.Content
                    className="z-[10002] overflow-hidden rounded-xl border border-[#E8EBF3] bg-white shadow-[0_16px_40px_rgba(26,22,47,0.12)] animate-in fade-in zoom-in-95 duration-100"
                    sideOffset={6}
                    align="start"
                    style={{
                        width: 'var(--radix-dropdown-menu-trigger-width)',
                        minWidth: 220,
                        maxWidth: 'calc(100vw - 16px)',
                    }}
                >
                    <div className="max-h-56 overflow-y-auto p-1.5 space-y-0.5">
                        {safeOptions.map((role) => {
                            const isSelected = role.id === value;

                            return (
                                <DropdownMenu.Item
                                    key={role.id}
                                    onSelect={() => {
                                        onChange(role.id);
                                        setOpen(false);
                                    }}
                                    className={clsx(
                                        "flex min-w-0 items-center justify-between gap-3 rounded-[10px] px-2.5 py-2.5 text-left text-sm outline-none transition-colors",
                                        isSelected ? 'bg-[#F4F5F8] text-[#1A162F]' : 'text-text-main hover:bg-[#F8F9FC]'
                                    )}
                                >
                                    <span className="font-medium truncate" title={formatSystemRoleLabel(role.name, t)}>
                                        {formatSystemRoleLabel(role.name, t)}
                                    </span>
                                    {isSelected ? <Check size={14} weight="regular" className="shrink-0 text-[#1A162F]" /> : null}
                                </DropdownMenu.Item>
                            );
                        })}
                    </div>
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
};

const PermissionCheckbox = ({
    checked,
    disabled,
    onChange,
    ariaLabel,
    className,
}: {
    checked: boolean;
    disabled?: boolean;
    onChange: (checked: boolean) => void;
    ariaLabel: string;
    className?: string;
}) => {
    return (
        <button
            type="button"
            role="checkbox"
            aria-checked={checked}
            aria-label={ariaLabel}
            onClick={() => onChange(!checked)}
            disabled={disabled}
            className={clsx(
                'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1A162F]/20',
                checked
                    ? 'border-[#1A162F] bg-[#1A162F] text-white'
                    : 'border-[#C8D0E2] bg-white text-transparent hover:border-[#8A93B2]',
                disabled && 'cursor-not-allowed opacity-50',
                className
            )}
        >
            <Check size={12} weight="bold" className="pointer-events-none" />
        </button>
    );
};

// ─── System Access Toggle ───────────────────────────────────────

const SystemAccessToggle = ({
    title,
    description,
    checked,
    disabled,
    onChange,
}: {
    title: string;
    description: string;
    checked: boolean;
    disabled?: boolean;
    onChange: (checked: boolean) => void;
}) => {
    const handleToggle = () => {
        if (disabled) return;
        onChange(!checked);
    };

    return (
        <div
            onClick={handleToggle}
            className={clsx(
                "flex items-start justify-between border-b border-[#ECEFF5] py-3 px-4 -mx-4 first:rounded-t-xl last:rounded-b-xl last:border-0 transition-colors duration-150 select-none",
                disabled
                    ? "cursor-not-allowed opacity-50"
                    : "cursor-pointer hover:bg-[#F8F9FC]"
            )}
        >
            <div className="pr-4 flex-1">
                <p className="text-sm font-medium text-text-main">{title}</p>
                <p className="mt-1 text-xs leading-5 text-[#666F8D]">{description}</p>
            </div>
            <div className="flex items-center mt-1 shrink-0">
                <PermissionCheckbox
                    checked={checked}
                    disabled={disabled}
                    onChange={onChange}
                    ariaLabel={title}
                />
            </div>
        </div>
    );
};

const SystemAccessSkeleton = () => (
    <div className="rounded-xl border border-[#E8EBF3] bg-[#FCFCFC] px-5" aria-busy="true">
        {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="flex items-start justify-between border-b border-[#ECEFF5] py-3 last:border-0">
                <div className="animate-pulse pr-4">
                    <div className="h-4 w-48 rounded-md bg-[#D6DBE7]" />
                    <div className="mt-2 h-3 w-72 max-w-[52vw] rounded-md bg-[#ECEFF5]" />
                </div>
                <div className="mt-1 h-5 w-5 shrink-0 animate-pulse rounded-md bg-[#D6DBE7]" />
            </div>
        ))}
    </div>
);

// ─── Role Drawer ──────────────────────────────────────────────

const RoleDrawer = ({
    role, existingRoles, assignedMemberCount, isPreparing = false, onClose, onSave
}: {
    role: Role;
    existingRoles: Role[];
    assignedMemberCount: number;
    isPreparing?: boolean;
    onClose: () => void;
    onSave: (r: Role) => Promise<void> | void;
}) => {
    const { showDialog } = useDialog();
    const { t } = useTranslation();
    const isNew = role.id === '__new__';
    const [form, setForm] = useState<Role>(role);
    const [isSaving, setIsSaving] = useState(false);
    const normalizedRoleName = form.name.trim();
    const hasRoleName = normalizedRoleName.length > 0;
    const canEditRoleDetails = canEditRoleMetadata(role);
    const hasDuplicateRoleName = useMemo(
        () => hasVisibleDuplicateRoleNameError({
            existingRoles,
            currentRoleId: role.id,
            roleName: normalizedRoleName,
            isSaving,
        }),
        [existingRoles, isSaving, normalizedRoleName, role.id]
    );

    useEffect(() => {
        setForm(role);
    }, [role]);

    const closeDrawer = () => {
        if (isSaving) {
            return;
        }

        onClose();
    };

    const handleSaveRole = async () => {
        if (!isNew) {
            const confirmed = await showDialog({
                title: t('website.membersSettings.saveRoleChangesTitle'),
                subtitle: assignedMemberCount > 0
                    ? t('website.membersSettings.saveRoleChangesWithUsers', {
                        count: assignedMemberCount,
                        plural: assignedMemberCount === 1 ? '' : 's',
                    })
                    : t('website.membersSettings.saveRoleChangesDescription'),
                confirmText: t('website.settingsModal.saveChanges'),
                cancelText: t('common.cancel'),
            });

            if (!confirmed) {
                return;
            }
        }

        const nextRole = {
            ...form,
            id: isNew ? `custom-${Date.now()}` : form.id,
        };

        setIsSaving(true);
        try {
            await onSave(nextRole);
            onClose();
        } finally {
            setIsSaving(false);
        }
    };

    return createPortal(
        <>
            <div className="fixed inset-0 z-[500] bg-black/40 backdrop-blur-sm" onClick={closeDrawer} />
            <div className="fixed right-0 top-0 bottom-0 z-[510] flex w-[760px] max-w-[96vw] animate-in slide-in-from-right duration-300 ease-out flex-col rounded-l-[24px] border-l border-white/70 bg-white shadow-[-24px_0_60px_rgba(26,22,47,0.16)]">
                <div className="flex items-center justify-between border-b border-[#ECEFF5] px-6 py-4">
                    <h3 className="text-lg font-medium tracking-[-0.01em] text-text-main">
                        {isNew ? t('website.membersSettings.newRole') : t('website.membersSettings.editRole')}
                    </h3>
                    <button
                        onClick={closeDrawer}
                        disabled={isSaving}
                        aria-label={t('common.cancel')}
                        className="flex h-8 w-8 items-center justify-center rounded-xl text-[#8A93B2] transition-colors hover:bg-[#F4F5F8] hover:text-[#1A162F] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        ✕
                    </button>
                </div>

                <div className="flex-1 overflow-y-scroll [scrollbar-gutter:stable] custom-scrollbar px-6 py-4 space-y-4">
                    {/* Role name */}
                    <div>
                        <label className="mb-1 block text-sm font-medium text-text-main">
                            {t('website.membersSettings.roleName')}
                            <span className="ml-1 text-red-500">*</span>
                        </label>
                        <input
                            value={form.name}
                            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                            disabled={!canEditRoleDetails || isPreparing || isSaving}
                            placeholder={t('website.membersSettings.roleNamePlaceholder')}
                            className={clsx(
                                "w-full rounded-[10px] border px-3 py-2 text-sm font-normal text-[#1A162F] transition-all placeholder:text-[#8A93B2] disabled:bg-[#F8F9FC] disabled:text-[#8A93B2] disabled:opacity-50",
                                hasDuplicateRoleName
                                    ? "border-red-300 focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-100"
                                    : "border-[#E8EBF3] focus:border-[#1A162F] focus:outline-none focus:ring-2 focus:ring-[#1A162F]/20"
                            )}
                        />
                        {hasDuplicateRoleName ? (
                            <p className="mt-1.5 text-xs text-red-600">
                                {t('website.membersSettings.roleNameExists')}
                            </p>
                        ) : null}
                    </div>

                    <div className="space-y-3">
                        <h4 className="text-sm font-semibold text-[#1A162F]">{t('website.membersSettings.workspacePermissions')}</h4>
                        {isPreparing ? (
                            <SystemAccessSkeleton />
                        ) : (
                            <div className="rounded-xl border border-[#E8EBF3] bg-[#FCFCFC] px-5">
                                <SystemAccessToggle
                                    title={t('website.membersSettings.manageWorkspaceSettings')}
                                    description={t('website.membersSettings.manageWorkspaceSettingsDescription')}
                                    checked={form.permissions.canManageWorkspaceSettings}
                                    disabled={!form.isEditable || isPreparing || isSaving}
                                    onChange={v =>
                                        setForm(f => ({
                                            ...f,
                                            permissions: { ...f.permissions, canManageWorkspaceSettings: v },
                                        }))
                                    }
                                />
                                <SystemAccessToggle
                                    title={t('website.membersSettings.manageUsers')}
                                    description={t('website.membersSettings.manageUsersDescription')}
                                    checked={form.permissions.canManageMembers}
                                    disabled={!form.isEditable || isPreparing || isSaving}
                                    onChange={v =>
                                        setForm(f => ({
                                            ...f,
                                            permissions: { ...f.permissions, canManageMembers: v },
                                        }))
                                    }
                                />
                                <SystemAccessToggle
                                    title={t('website.membersSettings.manageRolesAndAccess')}
                                    description={t('website.membersSettings.manageRolesAndAccessDescription')}
                                    checked={form.permissions.canManageRoles}
                                    disabled={!form.isEditable || isPreparing || isSaving}
                                    onChange={v =>
                                        setForm(f => ({
                                            ...f,
                                            permissions: { ...f.permissions, canManageRoles: v },
                                        }))
                                    }
                                />
                                <SystemAccessToggle
                                    title={t('website.membersSettings.manageIntegrations')}
                                    description={t('website.membersSettings.manageIntegrationsDescription')}
                                    checked={form.permissions.canManageIntegrations}
                                    disabled={!form.isEditable || isPreparing || isSaving}
                                    onChange={v =>
                                        setForm(f => ({
                                            ...f,
                                            permissions: { ...f.permissions, canManageIntegrations: v },
                                        }))
                                    }
                                />
                                <SystemAccessToggle
                                    title={t('website.membersSettings.manageBilling')}
                                    description={t('website.membersSettings.manageBillingDescription')}
                                    checked={form.permissions.canManageBilling}
                                    disabled={!form.isEditable || isPreparing || isSaving}
                                    onChange={v =>
                                        setForm(f => ({
                                            ...f,
                                            permissions: { ...f.permissions, canManageBilling: v },
                                        }))
                                    }
                                />
                                <SystemAccessToggle
                                    title={t('website.membersSettings.viewUsage')}
                                    description={t('website.membersSettings.viewUsageDescription')}
                                    checked={form.permissions.canViewUsage}
                                    disabled={!form.isEditable || isPreparing || isSaving}
                                    onChange={v =>
                                        setForm(f => ({
                                            ...f,
                                            permissions: { ...f.permissions, canViewUsage: v },
                                        }))
                                    }
                                />
                                <SystemAccessToggle
                                    title={t('website.membersSettings.viewOutcomeSummary')}
                                    description={t('website.membersSettings.viewOutcomeSummaryDescription')}
                                    checked={form.permissions.canViewOutcomeSummary}
                                    disabled={!form.isEditable || isPreparing || isSaving}
                                    onChange={v =>
                                        setForm(f => ({
                                            ...f,
                                            permissions: { ...f.permissions, canViewOutcomeSummary: v },
                                        }))
                                    }
                                />
                                <SystemAccessToggle
                                    title={t('website.membersSettings.manageOutcomeSummary')}
                                    description={t('website.membersSettings.manageOutcomeSummaryDescription')}
                                    checked={form.permissions.canManageOutcomeSummary}
                                    disabled={!form.isEditable || isPreparing || isSaving}
                                    onChange={v =>
                                        setForm(f => ({
                                            ...f,
                                            permissions: { ...f.permissions, canManageOutcomeSummary: v },
                                        }))
                                    }
                                />
                                <SystemAccessToggle
                                    title={t('website.membersSettings.viewAuditLog')}
                                    description={t('website.membersSettings.viewAuditLogDescription')}
                                    checked={form.permissions.canViewAuditLog}
                                    disabled={!form.isEditable || isPreparing || isSaving}
                                    onChange={v =>
                                        setForm(f => ({
                                            ...f,
                                            permissions: { ...f.permissions, canViewAuditLog: v },
                                        }))
                                    }
                                />
                            </div>
                        )}
                    </div>
                </div>

                {form.isEditable ? (
                    <div className="flex justify-end gap-3 border-t border-[#ECEFF5] px-6 py-4">
                        <button
                            onClick={closeDrawer}
                            disabled={isSaving}
                            className="rounded-[10px] border border-[#E8EBF3] bg-white px-4 py-2.5 text-sm font-medium text-[#1A162F] transition-colors hover:bg-[#F8F9FC] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            onClick={handleSaveRole}
                            disabled={!hasRoleName || hasDuplicateRoleName || isSaving || isPreparing}
                            className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                        >
                            {isSaving
                                ? t('website.settingsModal.saving')
                                : (isNew ? t('website.membersSettings.createRole') : t('website.settingsModal.saveChanges'))}
                        </button>
                    </div>
                ) : null}
            </div>
        </>,
        document.body
    );
};

// ─── Role Row ─────────────────────────────────────────────────

const EMPTY_ROLE: Role = {
    id: '__new__',
    name: '',
    description: '',
    isSystem: false,
    isEditable: true,
    isDeletable: true,
    memberCount: 0,
    permissions: {
        connections: [],
        canManageWorkspaceSettings: false,
        canManageMembers: false,
        canManageRoles: false,
        canManageIntegrations: false,
        canManageConnectionAccess: false,
        canManageBilling: false,
        canViewUsage: false,
        canViewOutcomeSummary: false,
        canManageOutcomeSummary: false,
        canViewAuditLog: false,
        canTransferOwnership: false,
    },
};

// ─── Main Roles Tab ───────────────────────────────────────────

interface RoleMember {
    userId: string;
    displayName: string;
    email: string;
    role: string;
    dataRoleName?: string;
    data_role_name?: string;
    status?: string;
}

interface RolesTabProps {
    roles: Role[];
    onRolesChange: (updater: Role[] | ((prev: Role[]) => Role[])) => void;
    members: RoleMember[];
    onReassignMembersRole: (memberIds: string[], nextRole: string) => Promise<void>;
    onSaveRole?: (role: Role) => Promise<Role>;
    onDeleteRole?: (role: Role) => Promise<void>;
    onPrepareRole?: (role: Role) => Promise<Role>;
    canManageRoles: boolean;
    isLoading?: boolean;
}

const RolesListSkeleton = () => (
    <div className="animate-pulse divide-y divide-[#ECEFF5]">
        {[0, 1, 2].map(i => (
            <div key={i} className="flex items-center justify-between px-5 py-4">
                <div className="flex min-w-0 flex-col gap-2">
                    <div className="flex items-center gap-2">
                        <div className="h-4 w-24 rounded-md bg-[#D6DBE7]" />
                        <div className="h-5 w-14 rounded-md bg-[#ECEFF5]" />
                    </div>
                    <div className="h-3 w-16 rounded-md bg-[#ECEFF5]" />
                </div>
                <div className="flex items-center gap-1.5">
                    <div className="h-9 w-9 rounded-lg bg-[#ECEFF5]" />
                </div>
            </div>
        ))}
    </div>
);

const normalizeRoleKey = (role: string) => role.trim().toUpperCase();

const formatSystemRoleLabel = formatRoleLabel;

const resolveRoleAssignmentValue = (role: Role) => role.isSystem ? role.name.toUpperCase() : role.name;

const RolesTab = ({
    roles,
    onRolesChange,
    members,
    onReassignMembersRole,
    onSaveRole,
    onDeleteRole,
    onPrepareRole,
    canManageRoles,
    isLoading = false,
}: RolesTabProps) => {
    const { t } = useTranslation();
    const [editingRole, setEditingRole] = useState<Role | undefined>(undefined);
    const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);
    const [replacementRoleId, setReplacementRoleId] = useState('');
    const [membersPendingDeletionReassignment, setMembersPendingDeletionReassignment] = useState<RoleMember[]>([]);
    const [isReassigningMembers, setIsReassigningMembers] = useState(false);
    const [openingRoleId, setOpeningRoleId] = useState<string | null>(null);
    const roleDrawerRequestIdRef = useRef(0);
    const { showDialog } = useDialog();
    const { showToast } = useToast();
    const emptyRole = useMemo<Role>(() => ({
        ...EMPTY_ROLE,
        permissions: {
            ...EMPTY_ROLE.permissions,
            connections: [],
        },
    }), []);

    // Only Owner remains protected; every other role can be configured when the backend marks it editable/deletable.
    const configurableRoles = useMemo(() => roles.filter(r => {
        const roleKey = normalizeRoleKey(r.roleKey || r.name);
        if (roleKey === 'OWNER') return false;
        return roleKey === 'ADMIN' || roleKey === 'BILLING' || roleKey === 'MEMBER' || r.isEditable || r.isDeletable !== false || !r.isSystem;
    }), [roles]);
    const sortedRoles = useMemo(() => {
        const ROLE_LIST_PRIORITY = ['ADMIN', 'BILLING', 'MEMBER'];

        return [...configurableRoles].sort((a, b) => {
            const aPriority = ROLE_LIST_PRIORITY.indexOf(normalizeRoleKey(a.roleKey || a.name));
            const bPriority = ROLE_LIST_PRIORITY.indexOf(normalizeRoleKey(b.roleKey || b.name));
            const normalizedAPriority = aPriority === -1 ? ROLE_LIST_PRIORITY.length : aPriority;
            const normalizedBPriority = bPriority === -1 ? ROLE_LIST_PRIORITY.length : bPriority;

            return normalizedAPriority - normalizedBPriority;
        });
    }, [configurableRoles]);

    const getMembersUsingRole = (role: Role) => members.filter(member => {
        const memberStatus = String(member.status || '').toUpperCase();
        const isActiveMember = !memberStatus || memberStatus === 'ACTIVE';
        const assignedRoleName = member.dataRoleName || member.data_role_name || member.role;

        return isActiveMember && normalizeRoleKey(assignedRoleName) === normalizeRoleKey(role.name);
    });

    const membersToReassign = roleToDelete ? membersPendingDeletionReassignment : [];
    const replacementRoleOptions = useMemo(() => {
        if (!roleToDelete) {
            return [];
        }

        return getDeletionReplacementRoleOptions(roles, roleToDelete);
    }, [roleToDelete, roles]);

    const handleSave = async (updated: Role) => {
        const savedRole = onSaveRole ? await onSaveRole(updated) : updated;

        onRolesChange(prev => {
            const exists = prev.find(r => r.id === savedRole.id);
            return exists ? prev.map(r => r.id === savedRole.id ? savedRole : r) : [...prev, savedRole];
        });
    };

    const closeRoleDrawer = () => {
        roleDrawerRequestIdRef.current += 1;
        setOpeningRoleId(null);
        setEditingRole(undefined);
    };

    const openRoleDrawer = async (role: Role) => {
        if (!canManageRoles) {
            return;
        }

        const requestId = roleDrawerRequestIdRef.current + 1;
        roleDrawerRequestIdRef.current = requestId;
        setOpeningRoleId(role.id);
        setEditingRole(role);
        try {
            if (!onPrepareRole) {
                return;
            }

            const preparedRole = await onPrepareRole(role);
            if (roleDrawerRequestIdRef.current !== requestId) {
                return;
            }

            setEditingRole(currentRole =>
                currentRole?.id === role.id ? preparedRole : currentRole
            );
        } catch (error: any) {
            if (roleDrawerRequestIdRef.current === requestId) {
                setEditingRole(undefined);
                showToast(error.message || t('website.membersSettings.manageRolesPermissionDenied'), 'error');
            }
        } finally {
            if (roleDrawerRequestIdRef.current === requestId) {
                setOpeningRoleId(null);
            }
        }
    };

    const confirmAndDeleteRole = async (role: Role) => {
        const roleLabel = formatSystemRoleLabel(role.name, t);
        const confirmed = await showDialog({
            title: t('website.membersSettings.deleteRole'),
            subtitle: t('website.membersSettings.deleteRoleDescription', { role: roleLabel }),
            confirmText: t('common.delete'),
            cancelText: t('common.cancel'),
            destructive: true,
        });

        if (!confirmed) {
            return;
        }

        if (onDeleteRole) {
            await onDeleteRole(role);
        }
        onRolesChange(prev => prev.filter(item => item.id !== role.id));
    };

    const handleDelete = async (role: Role) => {
        if (normalizeRoleKey(role.roleKey || role.name) === 'OWNER' || role.isDeletable === false) {
            return;
        }

        const assignedMembers = getMembersUsingRole(role);
        if (assignedMembers.length > 0) {
            setMembersPendingDeletionReassignment(assignedMembers);
            setRoleToDelete(role);
            setReplacementRoleId('');
            return;
        }

        await confirmAndDeleteRole(role);
    };

    const handleReassignMembersAndContinue = async () => {
        if (!roleToDelete) {
            return;
        }

        const deletingRole = roleToDelete;
        const assignedMembers = membersPendingDeletionReassignment;
        const replacementRole = replacementRoleOptions.find(role => role.id === replacementRoleId);

        if (assignedMembers.length > 0 && !replacementRole) {
            return;
        }

        setIsReassigningMembers(true);
        try {
            if (assignedMembers.length > 0 && replacementRole) {
                await onReassignMembersRole(
                    assignedMembers.map(member => member.userId),
                    resolveRoleAssignmentValue(replacementRole)
                );
            }

            if (onDeleteRole) {
                await onDeleteRole(deletingRole);
            }

            // In this flow, reassignment modal is the explicit confirmation.
            // Delete the role directly after reassignment to avoid a second dialog.
            onRolesChange(prev => prev.filter(item => item.id !== deletingRole.id));
            setRoleToDelete(null);
            setReplacementRoleId('');
            setMembersPendingDeletionReassignment([]);
        } finally {
            setIsReassigningMembers(false);
        }
    };

    const RoleRow = ({ role }: { role: Role }) => {
        const canDelete = normalizeRoleKey(role.roleKey || role.name) !== 'OWNER' && role.isDeletable !== false;
        const memberCount = getMembersUsingRole(role).length;
        const showRoleActions = canManageRoles;
        const roleLabel = formatSystemRoleLabel(role.name, t);

        return (
            <div className="group flex items-center justify-between px-5 py-4 transition-colors hover:bg-[#F8F9FC]">
                <div className="flex min-w-0 flex-col">
                    <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm font-medium text-text-main">{roleLabel}</span>
                    </div>
                    <p className="mt-0.5 text-[11px] leading-5 text-[#8A93B2]">
                        {t('website.membersSettings.assignedTo', {
                            count: memberCount,
                            plural: memberCount === 1 ? '' : 's',
                        })}
                    </p>
                </div>
                <div className="flex items-center gap-1.5">
                    {showRoleActions ? (
                        <Tooltip content={t('website.membersSettings.editRole')} side="top">
                            <button
                                type="button"
                                onClick={() => {
                                    void openRoleDrawer(role);
                                }}
                                disabled={openingRoleId === role.id}
                                aria-label={t('website.membersSettings.editRoleAria', { role: roleLabel })}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-text-sub transition-colors hover:bg-gray-100 hover:text-text-main focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:cursor-wait disabled:opacity-60"
                            >
                                <PencilSimple size={20} weight="regular" />
                            </button>
                        </Tooltip>
                    ) : null}
                    {showRoleActions && canDelete ? (
                        <Tooltip content={t('website.membersSettings.deleteRole')} side="top">
                            <button
                                type="button"
                                onClick={() => {
                                    void handleDelete(role);
                                }}
                                aria-label={t('website.membersSettings.deleteRoleAria', { role: roleLabel })}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-text-sub transition-colors hover:bg-red-50 hover:text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-200"
                            >
                                <Trash size={20} weight="regular" />
                            </button>
                        </Tooltip>
                    ) : null}
                </div>
            </div>
        );
    };

    if (!canManageRoles) {
        // 数据还在加载中时，不显示无权限页面，而是显示骨架屏，避免闪烁
        if (isLoading) {
            return (
                <div className="space-y-5">
                    <div className="overflow-hidden rounded-xl border border-[#E8EBF3] bg-white shadow-[0_10px_24px_rgba(26,22,47,0.06)]">
                        <RolesListSkeleton />
                    </div>
                </div>
            );
        }

        return (
            <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                <p className="text-lg font-medium">{t('website.billing.permissionDenied')}</p>
                <p className="text-sm mt-2">{t('website.membersSettings.manageRolesPermissionDenied')}</p>
            </div>
        );
    }

    return (
        <div className="space-y-5">
            <div className="overflow-hidden rounded-xl border border-[#E8EBF3] bg-white shadow-[0_10px_24px_rgba(26,22,47,0.06)]">
                <div className="flex items-center justify-between border-b border-[#ECEFF5] px-5 py-4">
                    <p className="mt-0.5 text-sm leading-6 text-[#666F8D]">
                        {t('website.membersSettings.rolesIntro')}
                    </p>
                    <button
                        onClick={() => {
                            void openRoleDrawer(emptyRole);
                        }}
                        disabled={openingRoleId === emptyRole.id}
                        className="flex items-center gap-2 px-3 py-2 text-sm bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors shrink-0 disabled:cursor-wait disabled:opacity-60"
                    >
                        <Plus size={14} weight="regular" />
                        {t('website.membersSettings.newRole')}
                    </button>
                </div>

                {/* Role list */}
                {isLoading ? (
                    <RolesListSkeleton />
                ) : (
                    <>
                        <div className="divide-y divide-[#ECEFF5]">
                            {sortedRoles.map(r => <RoleRow key={r.id} role={r} />)}
                        </div>

                        {sortedRoles.length === 0 && (
                            <div className="px-5 py-10 text-center">
                                <p className="text-sm font-medium text-text-main">{t('website.membersSettings.noRolesYet')}</p>
                                <p className="mt-1 text-xs text-[#666F8D]">{t('website.membersSettings.noRolesYetDescription')}</p>
                            </div>
                        )}
                    </>
                )}
            </div>

            {editingRole !== undefined && (
                <RoleDrawer
                    role={editingRole}
                    existingRoles={roles}
                    assignedMemberCount={editingRole.id === '__new__' ? 0 : getMembersUsingRole(editingRole).length}
                    isPreparing={openingRoleId === editingRole.id}
                    onClose={closeRoleDrawer}
                    onSave={handleSave}
                />
            )}

            <Modal
                isOpen={roleToDelete !== null}
                onClose={() => {
                    if (isReassigningMembers) return;
                    setRoleToDelete(null);
                    setReplacementRoleId('');
                    setMembersPendingDeletionReassignment([]);
                }}
                title={t('website.membersSettings.reassignUsersBeforeDeleting')}
                width="max-w-xl"
                bodyClassName="px-6 pb-6 pt-4"
                showHeaderDivider
            >
                <div className="space-y-4">
                    <p className="text-sm leading-relaxed text-[#666F8D]">
                        {t('website.membersSettings.reassignRoleBeforeDeleteDescription', {
                            role: formatSystemRoleLabel(roleToDelete?.name, t),
                            count: membersToReassign.length,
                            plural: membersToReassign.length === 1 ? '' : 's',
                        })}
                    </p>

                    <div>
                        <label className="mb-1.5 block text-sm font-medium text-text-main">
                            {t('website.membersSettings.newRoleForTheseUsers')}
                        </label>
                        <ReplacementRoleDropdown
                            value={replacementRoleId}
                            onChange={setReplacementRoleId}
                            options={replacementRoleOptions}
                            disabled={isReassigningMembers}
                        />
                        {replacementRoleOptions.length === 0 ? (
                            <p className="mt-1 text-xs text-red-600">
                                {t('website.membersSettings.noReplacementRoleAvailable')}
                            </p>
                        ) : null}
                    </div>

                    <div className="overflow-hidden rounded-xl border border-[#E8EBF3]">
                        <div className="bg-[#F8F9FC] px-3 py-2 text-[11px] font-medium text-[#8A93B2]">
                            {t('website.membersSettings.usersUsingThisRole')}
                        </div>
                        <div className="max-h-52 overflow-y-auto divide-y divide-[#ECEFF5]">
                            {membersToReassign.map(member => (
                                <div key={member.userId} className="px-3 py-2.5">
                                    <div className="text-sm font-medium text-text-main">{member.displayName}</div>
                                    <div className="text-xs text-[#666F8D]">{member.email}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-1">
                        <button
                            onClick={() => {
                                setRoleToDelete(null);
                                setReplacementRoleId('');
                                setMembersPendingDeletionReassignment([]);
                            }}
                            disabled={isReassigningMembers}
                            className="rounded-[10px] border border-[#E8EBF3] bg-white px-4 py-2.5 text-sm font-medium text-[#1A162F] transition-colors hover:bg-[#F8F9FC] disabled:opacity-50"
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            onClick={() => {
                                void handleReassignMembersAndContinue();
                            }}
                            disabled={
                                isReassigningMembers
                                || (
                                    membersToReassign.length > 0
                                    && (!replacementRoleId || replacementRoleOptions.length === 0)
                                )
                            }
                            className="flex items-center justify-center gap-2 rounded-[10px] bg-[#1A162F] px-4 py-2.5 text-sm font-medium text-white shadow-[0_10px_20px_rgba(26,22,47,0.12)] transition-colors hover:bg-[#231D3E] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {isReassigningMembers ? (
                                <>
                                    <CircleNotch className="w-4 h-4 animate-spin" />
                                    <span>{t('website.membersSettings.reassigning')}</span>
                                </>
                            ) : (
                                t('website.membersSettings.reassignAndDeleteRole')
                            )}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default RolesTab;
export { EMPTY_ROLE, RoleDrawer };
